import React, { useState } from 'react';
import { Button, Checkbox, Dialog, DialogActions, DialogContent, FormControl, InputLabel, NativeSelect, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import SendIcon from '@mui/icons-material/Send';
import OrderConfirmationOptions from '../domain/orderconfirmationoptions';
import { useTranslation } from 'react-i18next';
import languages from '../i18n/languages';

export default function OrderConfirmationMailInfoInputDialog(props) {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const [priceChecked, setPriceChecked] = useState([]);
    const [unitPriceChecked, setUnitPriceChecked] = useState([]);
    const [productNameChecked, setProductNameChecked] = useState([]);
    const { t, i18n } = useTranslation();
    const [language, setLanguage] = useState(i18n.language);
    const allPriceChecked = priceChecked.length === props.orders.length;
    const allUnitPriceChecked = unitPriceChecked.length === props.orders.length;
    const allProductNameChecked = productNameChecked.length === props.orders.length;

    const onConfirm = () => {
        const selectedOptions = props.orders.map(
            (order) =>
                new OrderConfirmationOptions({
                    orderId: order.id,
                    basicPriceInfo: priceChecked.indexOf(order.id) > -1,
                    unitPriceInfo: unitPriceChecked.indexOf(order.id) > -1,
                    productNameInfo: productNameChecked.indexOf(order.id) > -1,
                })
        );
        props.save(language, selectedOptions);
    };

    const onCancel = () => {
        props.cancel();
    };

    const onPriceCheckBoxChange = (orderid) => {
        if (props.combineOrders) {
            if (priceChecked.length > 0) {
                setPriceChecked([]);
                setUnitPriceChecked([]);
            } else {
                setPriceChecked(props.orders.map((o) => o.id));
            }
            return;
        }

        const currentIndex = priceChecked.indexOf(orderid);
        const newPriceChecked = [...priceChecked];

        if (currentIndex === -1) {
            newPriceChecked.push(orderid);
        } else {
            newPriceChecked.splice(currentIndex, 1);
            const unitPriceIndex = unitPriceChecked.indexOf(orderid);
            if (unitPriceIndex > -1) {
                const newUnitPriceChecked = [...unitPriceChecked];
                newUnitPriceChecked.splice(unitPriceIndex, 1);
                setUnitPriceChecked(newUnitPriceChecked);
            }
        }
        setPriceChecked(newPriceChecked);
    };

    const onUnitPriceCheckBoxChange = (orderid) => {
        if (props.combineOrders) {
            unitPriceChecked.length > 0 ? setUnitPriceChecked([]) : setUnitPriceChecked(props.orders.map((o) => o.id));
            return;
        }

        const currentIndex = unitPriceChecked.indexOf(orderid);
        const newUnitPriceChecked = [...unitPriceChecked];

        if (currentIndex === -1) {
            newUnitPriceChecked.push(orderid);
        } else {
            newUnitPriceChecked.splice(currentIndex, 1);
        }
        setUnitPriceChecked(newUnitPriceChecked);
    };

    const onProductNameCheckBoxChange = (orderId) => {
        if (props.combineOrders) {
            if (productNameChecked.length > 0) {
                setProductNameChecked([]);
            } else {
                setProductNameChecked(props.orders.map((o) => o.id));
            }
            return;
        }

        const currentIndex = productNameChecked.indexOf(orderId);
        const newProductNameChecked = [...productNameChecked];

        if (currentIndex === -1) {
            newProductNameChecked.push(orderId);
        } else {
            newProductNameChecked.splice(currentIndex, 1);
        }
        setProductNameChecked(newProductNameChecked);
    };

    const onLanguageChange = (selectedLanguage) => {
        setLanguage(selectedLanguage);
    };

    const handleAllPriceChecked = () => {
        if (allPriceChecked) {
            setPriceChecked([]);
            setUnitPriceChecked([]);
        } else {
            setPriceChecked(props.orders.map((o) => o.id));
        }
    };

    const handleAllUnitPriceChecked = () => {
        if (allUnitPriceChecked) {
            setUnitPriceChecked([]);
        } else {
            // Only select unit prices for orders where price is checked
            setUnitPriceChecked(priceChecked);
        }
    };

    const handleAllProductNameChecked = () => {
        if (allProductNameChecked) {
            setProductNameChecked([]);
        } else {
            setProductNameChecked(props.orders.map((o) => o.id));
        }
    };

    return (
        <Dialog
            onClose={onCancel}
            aria-labelledby="simple-dialog-title"
            open={props.open}
            fullWidth={true}
            maxWidth={'md'}
            fullScreen={fullScreen}>
            <DialogContent dividers>
                <h3>{t('order.orderConfirmationContent')}</h3>
                <p>
                    <strong>{t('order.totalPrice')}: </strong>
                    {t('order.confirmationContentHint')}
                    <br />
                    <strong>{t('order.unitPrices')}: </strong>
                    {t('order.filterlinesContainTotalPricesForAFilter')}
                    <br />
                    <strong>{t('order.productNames')}: </strong>
                    {t('order.productNamesHint')}
                </p>
                <FormControl sx={{ mt: 2, ml: 1, mr: 1, width: '95%' }}>
                    <InputLabel id={'select-confirmation-email-language'}>
                        {t('order.orderConfirmationLanguage')}
                    </InputLabel>
                    <NativeSelect
                        id="confirmation-language-select"
                        name="confirmation-language-select"
                        required={true}
                        value={language}
                        onChange={(evt) => onLanguageChange(evt.target.value)}>
                        {languages.map((lang) => (
                            <option key={lang.lang} value={lang.lang}>
                                {t(lang.name)}
                            </option>
                        ))}
                    </NativeSelect>
                </FormControl>
                <TableContainer component={Paper}>
                    <Table aria-label="simple table">
                        <TableHead>
                            <TableRow>
                                <TableCell>{t('general.location')}</TableCell>
                                {/* for combined order no top level checkboxes */}
                                {props.combineOrders ? (
                                    <React.Fragment>
                                        <TableCell align="right">{t('order.totalPrice')}</TableCell>
                                        <TableCell align="right">{t('order.unitPrices')}</TableCell>
                                        <TableCell align="right">{t('order.productNames')}</TableCell>
                                    </React.Fragment>
                                ) : (
                                    <React.Fragment>
                                        <TableCell align="right">
                                            <Checkbox
                                                data-testid="select-all-total-price"
                                                checked={allPriceChecked}
                                                indeterminate={priceChecked.length > 0 && !allPriceChecked}
                                                onChange={handleAllPriceChecked}
                                                inputProps={{ 'aria-label': 'select all total price' }}
                                            />
                                            {t('order.totalPrice')}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Checkbox
                                                data-testid="select-all-unit-price"
                                                checked={allUnitPriceChecked}
                                                indeterminate={unitPriceChecked.length > 0 && !allUnitPriceChecked}
                                                onChange={handleAllUnitPriceChecked}
                                                inputProps={{ 'aria-label': 'select all unit prices' }}
                                            />
                                            {t('order.unitPrices')}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Checkbox
                                                data-testid="select-all-product-name"
                                                checked={allProductNameChecked}
                                                indeterminate={productNameChecked.length > 0 && !allProductNameChecked}
                                                onChange={handleAllProductNameChecked}
                                                inputProps={{ 'aria-label': 'select all product names' }}
                                            />
                                            {t('order.productNames')}
                                        </TableCell>
                                    </React.Fragment>
                                )}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {props.combineOrders ? (
                                <TableRow>
                                    <TableCell>{t('order.combinedOrder')}</TableCell>
                                    <TableCell align="right">
                                        <Checkbox
                                            data-testid="price-checkbox-combined"
                                            checked={priceChecked.length > 0}
                                            onChange={() => onPriceCheckBoxChange(0)}
                                            inputProps={{ 'aria-label': 'primary checkbox' }}
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <Checkbox
                                            data-testid="unit-price-checkbox-combined"
                                            checked={unitPriceChecked.length > 0}
                                            disabled={priceChecked.length > 0 ? false : true}
                                            onChange={() => onUnitPriceCheckBoxChange(0)}
                                            inputProps={{ 'aria-label': 'primary checkbox' }}
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <Checkbox
                                            data-testid="product-name-checkbox-combined"
                                            checked={productNameChecked.length > 0}
                                            onChange={() => onProductNameCheckBoxChange(0)}
                                            inputProps={{ 'aria-label': 'primary checkbox' }}
                                        />
                                    </TableCell>
                                </TableRow>
                            ) : (
                                props.orders.map((order) => (
                                    <TableRow key={order.id}>
                                        <TableCell>
                                            {order.location ? order.location.name : order.deliveryRecipient}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Checkbox
                                                data-testid={`price-checkbox-${order.id}`}
                                                checked={priceChecked.indexOf(order.id) > -1}
                                                onChange={() => onPriceCheckBoxChange(order.id)}
                                                inputProps={{ 'aria-label': 'primary checkbox' }}
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            <Checkbox
                                                data-testid={`unit-price-checkbox-${order.id}`}
                                                disabled={priceChecked.indexOf(order.id) < 0}
                                                checked={unitPriceChecked.indexOf(order.id) > -1}
                                                onChange={() => onUnitPriceCheckBoxChange(order.id)}
                                                inputProps={{ 'aria-label': 'primary checkbox' }}
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            <Checkbox
                                                data-testid={`product-name-checkbox-${order.id}`}
                                                checked={productNameChecked.indexOf(order.id) > -1}
                                                onChange={() => onProductNameCheckBoxChange(order.id)}
                                                inputProps={{ 'aria-label': 'primary checkbox' }}
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </DialogContent>
            <DialogActions>
                <Button
                    variant="contained"
                    color="primary"
                    name="btn-confirm-options"
                    onClick={onConfirm}
                    disabled={props.disable}
                    sx={{ m: 1 }}
                    endIcon={<SendIcon />}>
                    {t('buttons.send')}
                </Button>
                <Button
                    variant="contained"
                    color="secondary"
                    name="btn-cancel-options"
                    onClick={onCancel}
                    sx={{ m: 1 }}>
                    {t('buttons.cancel')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
